import Link from 'next/link';
import {
  Box,
  IconButton,
  SystemIcon,
  Typography,
} from '@wishket/design-system';
import { twJoin } from 'tailwind-merge';

import ArticleAuthor from './ArticleAuthor';
import ArticleInfo from './ArticleInfo';
import ArticleItemImage from './ArticleItemImage';

import { ArticleItemProps } from '@/shared/types';
import { PATH } from '@/shared/constants';
import { ScrapIconButton } from '@/shared/Components';

const ArticleItem = ({
  id,
  href,
  title,
  category,
  categoryHref,
  description,
  thumbnail,
  articleInfo,
  author,
  isVisibleCategory,
  isVisibleUtil = true,
  openInNewTab = false,
  onClickDelete,
  isScrapped,
  isScrapBoxPositionLeft,
}: ArticleItemProps) => {
  return (
    <article className="flex w-full flex-col gap-1.5">
      {isVisibleCategory && (
        <Box className="h-6">
          <Link
            href={categoryHref}
            data-testid="contentsItem-category-link"
            target={openInNewTab ? '_blank' : '_self'}
          >
            <Typography
              variant="body2"
              className="text-w-gray-500 hover:underline"
            >
              {category}
            </Typography>
          </Link>
        </Box>
      )}
      <Box className="flex flex-col gap-2">
        <Link
          href={href}
          target={openInNewTab ? '_blank' : '_self'}
          data-testid="contentsItem-item-link"
        >
          <Box className="group flex justify-between gap-4 tablet:gap-12">
            <Box className="group flex justify-between gap-4 tablet:gap-12">
              <Box className="flex flex-col gap-3">
                <Box className="flex flex-col gap-2 tablet:gap-1">
                  <Typography
                    as="h3"
                    variant="body1"
                    className="line-clamp-2 font-medium text-w-gray-900 group-hover:text-w-gray-600 tablet:line-clamp-1 tablet:typo-subTitle1"
                  >
                    {title}
                  </Typography>
                  <ArticleInfo {...articleInfo} />
                  <Box
                    className="hidden tablet:block"
                    data-testid="contentsItem-description"
                  >
                    <Typography
                      variant="body2"
                      className="line-clamp-2 text-w-gray-500"
                    >
                      {description}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              className={twJoin(
                'flex-none duration-200 ease-in',
                'desktop:group-hover:-translate-y-[5%]'
              )}
            >
              <ArticleItemImage
                width={110}
                height={110}
                alt={title}
                sizes="110px"
                src={
                  thumbnail ||
                  `${PATH.MEDIA_YOZM_IMAGE}/default_thumbnail_article.png`
                }
                className="size-[84px] rounded-2xl border border-w-gray-100 object-cover tablet:size-[110px]"
                priority
                loading="eager"
              />
            </Box>
          </Box>
        </Link>
        <Box className="flex items-center justify-between tablet:mr-[158px]">
          <ArticleAuthor {...author} openInNewTab={openInNewTab} />
          {/* TODO: 컨텐츠아이템컴포넌트의 아이콘은 추후 정책이 정해졌을시, feature로 생성한다.(이때 프롭스도 정의)*/}
          {isVisibleUtil && (
            <Box className="flex gap-1">
              <ScrapIconButton
                id={id}
                isScrapBoxPositionLeft={isScrapBoxPositionLeft}
                authorHref={author.href}
                initialIsScrapped={isScrapped}
              />
              {onClickDelete && (
                <IconButton size="md" onClick={() => onClickDelete(id)}>
                  <SystemIcon name="medium_minus" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </article>
  );
};

export default ArticleItem;
